import React from 'react'
import { connect } from 'react-redux'
import { Advert } from '@chegg-wt/core/components/Advert'
import { useRoutesContext } from '@chegg-wt/core/components/RoutesContext'
import { CitationFlowPage } from '@chegg-wt/core/types/citation-flow'
import { SOURCE_PAGE, SEARCH_PAGE } from '@chegg-wt/core/constants/citationFlow'
import {
  origins,
  UserClickPayload,
} from '@chegg-wt/core/state/instrumentation/rio/clickstreamInteractions'
import { userClick } from '@chegg-wt/core/state/instrumentation/actions'
import { Config } from '@chegg-wt/core/config'
import usePriceTest from '@chegg-wt/core/hooks/usePriceTest'
import { UpgradePromotionBoxCopy } from '@chegg-wt/core/types/copy'
import { UpgradeCopy } from '@chegg-wt/core/types/core'
import { authModalExpFlagSelector } from '@chegg-wt/core/state/createCitation/selectors'
import { OpenModalPayload } from '@chegg-wt/core/state/modals/types'
import { isGuestSelector } from '@chegg-wt/core/state/user/selectors'
import { AppState } from '@chegg-wt/core/state/types'
import { ONE_AUTH_MODAL } from '@chegg-wt/core/state/modals/constants'
import { openModal } from '@chegg-wt/core/state/modals/actions'
import AppLink from '../AppLink'
import { LvpMarginWrapper } from '../Layouts/LargeVideoPlayerLayout'
import UpgradePromotionBox from '../UpgradePromotionBox'
import { AUTH_MODAL_TITLE, AuthModalOrigin } from '../Modals/AuthModal/types'
import * as Styled from './styled'
import ListHeader from './ListHeader'
import BulletPoint from './BulletPoint'
import { TC_LINK } from './testIds'

interface Props {
  children: React.ReactNode
  copy: UpgradeCopy | undefined
  page?: CitationFlowPage
  upgradePromotionCopy?: UpgradePromotionBoxCopy
  authModalExpFlag?: boolean
  isGuestUser?: boolean
  userClick: (payload: UserClickPayload) => void
  openModal?: (payload: OpenModalPayload) => void
}

const UPGRADE_BUTTON = 'upgrade-button'

const UpgradeAdFrame: React.FunctionComponent<Props> = ({
  children,
  copy,
  page,
  upgradePromotionCopy,
  userClick,
  openModal,
  authModalExpFlag,
  isGuestUser,
}: Props) => {
  const showAuthModal = authModalExpFlag && isGuestUser
  const { toUpgradeCTA, toUpgrade } = useRoutesContext()
  const { pricingExperimentReady, freeTrial } = usePriceTest()

  const handleUpgradeClick = (page) => {
    const eventname = `click try ${Config.tokenSiteCode} plus`
    const name = `try ${Config.tokenSiteCode} plus`
    if (page === SOURCE_PAGE) {
      userClick({ origin: origins.upgradeSourcePageCTA, eventname, name })
    } else if (page === SEARCH_PAGE) {
      userClick({ origin: origins.upgradeSearchPageCTA, eventname, name })
    }
    if (showAuthModal) {
      openModal({
        name: ONE_AUTH_MODAL,
        data: {
          defaultFlow: 'signup',
          origin: AuthModalOrigin.SOURCE_SELECTION_PAGE,
          title: AUTH_MODAL_TITLE,
          redirectTo: toUpgrade(),
        },
      })
    }
  }

  if (!copy && !upgradePromotionCopy)
    return (
      <Styled.Container>
        <LvpMarginWrapper>
          <Styled.Wrapper>{children}</Styled.Wrapper>
          <Advert placement="C1" />
        </LvpMarginWrapper>
      </Styled.Container>
    )

  return (
    <Styled.Container>
      <LvpMarginWrapper>
        <Styled.Wrapper>
          {children}
          <Styled.UpgradeContainer>
            {upgradePromotionCopy ? (
              <UpgradePromotionBox {...upgradePromotionCopy} />
            ) : (
              <Styled.CardWrapper>
                <ListHeader header={copy.header} />
                <Styled.List>
                  {pricingExperimentReady &&
                    copy.body.map((point) => (
                      <BulletPoint point={point} freeTrial={freeTrial} />
                    ))}
                </Styled.List>
                {showAuthModal ? (
                  <Styled.RedirectButton
                    data-test={UPGRADE_BUTTON}
                    onClick={() => handleUpgradeClick(page)}
                  >
                    {copy.button}
                  </Styled.RedirectButton>
                ) : (
                  <AppLink route={toUpgradeCTA()}>
                    <Styled.RedirectButton
                      data-test={UPGRADE_BUTTON}
                      onClick={() => handleUpgradeClick(page)}
                    >
                      {copy.button}
                    </Styled.RedirectButton>
                  </AppLink>
                )}
                <Styled.TermsWrapper>
                  <Styled.TermsAndConditions
                    href={Config.trialTermsAndConditionsUrl}
                    data-test={TC_LINK}
                  >
                    *See Terms and Conditions
                  </Styled.TermsAndConditions>
                </Styled.TermsWrapper>
              </Styled.CardWrapper>
            )}
            <Styled.CenteredAdvert placement="C1" />
          </Styled.UpgradeContainer>
        </Styled.Wrapper>
      </LvpMarginWrapper>
    </Styled.Container>
  )
}

const mapDispatchToProps = {
  userClick,
  openModal,
}

const mapStateToProps = (state: AppState) => ({
  authModalExpFlag: authModalExpFlagSelector(state),
  isGuestUser: isGuestSelector(state),
})

export default connect(mapStateToProps, mapDispatchToProps)(UpgradeAdFrame)
